import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, GraduationCap, UserRound, KeyRound, User as UserIcon, Copy, Loader2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { id: "student", label: "Student", icon: GraduationCap },
  { id: "mentor", label: "Mentor", icon: UserRound },
  { id: "admin", label: "Admin", icon: ShieldCheck },
];

export const LoginCard = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("student");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [channel, setChannel] = useState("mock");
  const [mockOtp, setMockOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username, password });
      if (data.requires_2fa) {
        setOtpStep(true);
        setChannel(data.channel || "mock");
        setMockOtp(data.otp_mock || "");
        toast.info(data.channel === "mock"
          ? "Delivery unavailable — use the on-screen code"
          : `Code sent via ${data.channel === "sms" ? "SMS" : "WhatsApp"}`);
      } else {
        setUser(data.user);
        toast.success(`Welcome, ${data.user.name || data.user.username}`);
        navigate("/portal");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { username, otp });
      setUser(data.user);
      toast.success("Admin verified — welcome back");
      navigate("/portal");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copyOtp = () => {
    navigator.clipboard?.writeText(mockOtp);
    toast.success("Code copied");
  };

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-sky-100 bg-white/90 p-8 shadow-xl shadow-sky-900/5 backdrop-blur-xl sm:p-10"
      data-testid="login-card"
    >
      <div aria-hidden className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-100/80 blur-2xl" />

      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700">Portal Access</p>
      <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 sm:text-3xl">Sign in to your space</h2>

      <div className="mt-6 grid grid-cols-3 gap-2 rounded-2xl bg-sky-50 p-1.5" data-testid="role-tabs">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const active = role === r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => { setRole(r.id); setOtpStep(false); setError(""); }}
              data-testid={`role-tab-${r.id}`}
              className={`relative flex flex-col items-center gap-1 rounded-xl py-2.5 text-xs font-semibold transition-colors duration-200 ${
                active ? "text-sky-800" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {active && (
                <motion.span
                  layoutId="role-pill"
                  className="absolute inset-0 rounded-xl bg-white shadow-sm"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <Icon size={16} className="relative" />
              <span className="relative">{r.label}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {!otpStep ? (
          <motion.form
            key="credentials"
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 14 }}
            transition={{ duration: 0.3 }}
            onSubmit={handleLogin}
            className="mt-6 space-y-4"
            data-testid="login-form"
          >
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-500">Username</span>
              <div className="relative">
                <UserIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  placeholder="your username"
                  data-testid="login-username-input"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-500">Password</span>
              <div className="relative">
                <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </label>

            {error && (
              <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />}
              {loading ? "Checking…" : `Continue as ${ROLES.find((r) => r.id === role)?.label}`}
            </button>

            {role !== "admin" ? (
              <p className="pt-1 text-center text-xs leading-relaxed text-slate-500" data-testid="login-issued-note">
                {role === "mentor" ? "Mentor" : "Student"} credentials are created &amp; issued by the Admin only.
              </p>
            ) : (
              <p className="pt-1 text-center text-xs leading-relaxed text-slate-500">
                Admin sign-in requires a WhatsApp verification code.
              </p>
            )}
          </motion.form>
        ) : (
          <motion.form
            key="otp"
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.3 }}
            onSubmit={handleOtp}
            className="mt-6 space-y-4"
            data-testid="otp-form"
          >
            {channel === "mock" ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="otp-mock-banner">
                <p className="text-xs font-semibold uppercase tracking-widest text-amber-800">2FA · Mocked fallback</p>
                <p className="mt-1 text-xs leading-relaxed text-amber-700">
                  WhatsApp/SMS delivery was unavailable, so the code is shown here:
                </p>
                <button
                  type="button"
                  onClick={copyOtp}
                  data-testid="otp-copy-button"
                  className="mt-2 inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 font-mono text-lg font-bold tracking-[0.3em] text-amber-900 shadow-sm transition-colors duration-200 hover:bg-amber-100"
                >
                  {mockOtp} <Copy size={13} />
                </button>
              </div>
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="otp-sent-banner">
                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-800">
                  Code sent via {channel === "sms" ? "SMS" : "WhatsApp"}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                  A 6-digit verification code was sent to the admin&apos;s {channel === "sms" ? "phone via SMS" : "WhatsApp"}.
                  Enter it below — it expires in 10 minutes.
                </p>
              </div>
            )}

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-slate-500">Verification code</span>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                inputMode="numeric"
                placeholder="6-digit code"
                data-testid="otp-input"
                className="w-full rounded-xl border border-slate-200 bg-white py-3 px-4 text-center font-mono text-xl font-bold tracking-[0.4em] text-slate-900 outline-none transition-colors duration-200 placeholder:font-sans placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              />
            </label>

            {error && (
              <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600" data-testid="otp-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="otp-verify-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-600 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-amber-700 disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              {loading ? "Verifying…" : "Verify & enter"}
            </button>

            <button
              type="button"
              onClick={() => { setOtpStep(false); setOtp(""); setError(""); }}
              data-testid="otp-back-button"
              className="w-full text-center text-xs font-semibold text-slate-500 transition-colors duration-200 hover:text-sky-700"
            >
              ← Back to sign in
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
